/**
 * HTTP client with automatic Thread Auth Bearer token injection.
 *
 * All API calls go through this wrapper so auth headers are consistent.
 */

import { authClient } from "../lib/auth";
import { recordFeedbackEvent } from "../lib/feedbackContext";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

type MintSessionResponse = {
  thread_session?: { access_token?: string; refresh_token?: string } | null;
  user?: { email?: string } | null;
  flow360_api_key_stored?: boolean;
  flow360_access_token?: string;
  flow360_refresh_token?: string;
};

let flow360SessionMint: Promise<string | null> | null = null;

class AuthRecoveryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthRecoveryError";
  }
}

function getSessionStorage(): Storage | null {
  return typeof window === "undefined" ? null : window.sessionStorage;
}

function getFlow360AccessToken(): string | null {
  return getSessionStorage()?.getItem("flow360_access_token") ?? null;
}

function clearFlow360Session(): void {
  const storage = getSessionStorage();
  if (!storage) return;
  storage.removeItem("flow360_access_token");
  storage.removeItem("flow360_refresh_token");
  storage.removeItem("flow360_cognito_sub");
  storage.removeItem("flow360_user_email");
  storage.removeItem("flow360_api_key_stored");
  window.dispatchEvent(new Event("flow360-auth-ready"));
}

async function readErrorDetail(res: Response): Promise<string> {
  try {
    const body = await res.clone().json();
    const detail = body?.detail;
    if (typeof detail === "string") return detail;
    if (detail) return JSON.stringify(detail);
  } catch {
    // Fall back to text below.
  }
  return res.text().catch(() => "Unknown error");
}

async function mintThreadSessionFromFlow360(flow360Token: string): Promise<string | null> {
  const storage = getSessionStorage();
  const flow360RefreshToken = storage?.getItem("flow360_refresh_token");
  const body: Record<string, string> = { flow360_access_token: flow360Token };
  if (flow360RefreshToken) {
    body.flow360_refresh_token = flow360RefreshToken;
  }

  const res = await fetch(`${API_BASE}/flow360/auth/mint-session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await readErrorDetail(res);
    if (res.status === 401) {
      clearFlow360Session();
      throw new AuthRecoveryError("Flow360 session expired. Reconnect Flow360 to continue.");
    }
    throw new AuthRecoveryError(`Could not mint a Thread session from Flow360: ${detail}`);
  }

  const data = (await res.json()) as MintSessionResponse;
  const threadSession = data.thread_session;
  if (!threadSession?.access_token || !threadSession.refresh_token) {
    throw new AuthRecoveryError("Flow360 login succeeded but Thread session minting returned no token.");
  }

  const { error } = await authClient.auth.setSession({
    access_token: threadSession.access_token,
    refresh_token: threadSession.refresh_token,
  });
  if (error) {
    throw new AuthRecoveryError(`Could not store the minted Thread session: ${error.message}`);
  }

  if (data.flow360_access_token) {
    storage?.setItem("flow360_access_token", data.flow360_access_token);
  }
  if (data.flow360_refresh_token) {
    storage?.setItem("flow360_refresh_token", data.flow360_refresh_token);
  }
  if (data.user?.email) {
    storage?.setItem("flow360_user_email", data.user.email);
  }
  if (data.flow360_api_key_stored) {
    storage?.setItem("flow360_api_key_stored", "true");
  } else if (data.flow360_api_key_stored === false) {
    storage?.setItem("flow360_api_key_stored", "false");
  }

  return threadSession.access_token;
}

export async function getAuthToken({ forceFlow360Mint = false } = {}): Promise<string | null> {
  // E2E performance-profiling bypass. Backend only honors this token in
  // non-prod when THREAD_E2E_BYPASS_KEY is set and matches.
  if (typeof window !== "undefined" && !forceFlow360Mint) {
    const e2e = window.localStorage.getItem("thread_e2e_token");
    if (e2e) return e2e;
  }
  if (!forceFlow360Mint) {
    const {
      data: { session },
    } = await authClient.auth.getSession();
    if (session?.access_token) return session.access_token;
  }

  const flow360Token = getFlow360AccessToken();
  if (!flow360Token) return null;

  flow360SessionMint ??= mintThreadSessionFromFlow360(flow360Token).finally(() => {
    flow360SessionMint = null;
  });
  return flow360SessionMint;
}

function shouldRetryWithFlow360Mint(path: string, status: number): boolean {
  return status === 401 && !path.startsWith("/flow360/auth/mint-session") && !!getFlow360AccessToken();
}

export async function apiClient<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Forward Flow360 tokens in separate custom headers (never overwrite Thread Authorization)
  const storage = getSessionStorage();
  const flow360Token = storage?.getItem("flow360_access_token");
  if (flow360Token && path.startsWith("/flow360")) {
    headers["X-Flow360-Authorization"] = `Bearer ${flow360Token}`;
    const flow360User = storage?.getItem("flow360_user_email");
    if (flow360User) {
      headers["X-Flow360-Access-User"] = flow360User;
    }
    const flow360Refresh = storage?.getItem("flow360_refresh_token");
    if (flow360Refresh) {
      headers["X-Flow360-Refresh-Token"] = flow360Refresh;
    }
  }

  const body = options.body ? JSON.stringify(options.body) : undefined;
  const fetchApi = (authHeaders: Record<string, string>) =>
    fetch(`${API_BASE}${path}`, {
      ...options,
      headers: authHeaders,
      body,
    });

  let res = await fetchApi(headers);

  if (!res.ok && shouldRetryWithFlow360Mint(path, res.status)) {
    const retryToken = await getAuthToken({ forceFlow360Mint: true });
    if (retryToken && retryToken !== token) {
      res = await fetchApi({
        ...headers,
        Authorization: `Bearer ${retryToken}`,
      });
    }
  }

  if (!res.ok) {
    const text = await readErrorDetail(res);
    recordFeedbackEvent({
      type: "api_error",
      path,
      message: text,
      metadata: { status: res.status },
    });
    throw new Error(`API ${res.status}: ${text}`);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}
